// src/hooks/upload/useImageUploadQueue.js

import { useState, useEffect, useCallback } from "react";
import { compressImage } from "../../utils/imageCompression";
import { useUploadMedia } from "../media/useUploadMedia";
import {
    createUploadedImageObject,
    extractErrorMessage,
    showUploadSuccess,
    showUploadError,
} from "../../utils/upload/imageUploadHelpers";
import {
    getNextPendingItem,
    updateUploadStatus,
    removeUploadStatus,
    updateQueueItemStatus,
    removeFromQueue,
    updateErrorState,
    removeErrorState,
} from "../../utils/upload/uploadQueueManager";

/**
 * ✅ Custom hook for managing image upload queue
 */
const useImageUploadQueue = (value, onChange, maxSizeMB, isArabic) => {
    const [uploadQueue, setUploadQueue] = useState([]);
    const [uploadingImages, setUploadingImages] = useState({});
    const [errors, setErrors] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);

    const { uploadMedia } = useUploadMedia();

    // ✅ Upload single image
    const uploadSingleImage = useCallback(async (tempId, file) => {
        try {
            // Update status to compressing
            setUploadingImages((prev) =>
                updateUploadStatus(prev, tempId, 'compressing', 25)
            );

            // Compress image
            const compressedFile = await compressImage(file, maxSizeMB);

            // Update status to uploading
            setUploadingImages((prev) =>
                updateUploadStatus(prev, tempId, 'uploading', 50)
            );

            // Create FormData
            const formData = new FormData();
            formData.append('file', compressedFile);

            // Upload to server
            await new Promise((resolve, reject) => {
                uploadMedia(formData, {
                    onSuccess: (response) => {
                        const uploadedImage = createUploadedImageObject(response, compressedFile);

                        // Add to value array
                        onChange([...value, uploadedImage]);

                        // Update status to success
                        setUploadingImages((prev) =>
                            updateUploadStatus(prev, tempId, 'success', 100)
                        );

                        // Show success message
                        showUploadSuccess(isArabic);

                        // Remove from queue after delay
                        setTimeout(() => {
                            setUploadQueue((prev) => removeFromQueue(prev, tempId));
                            setUploadingImages((prev) => removeUploadStatus(prev, tempId));
                            resolve();
                        }, 800);
                    },
                    onError: (error) => {
                        const errorMsg = extractErrorMessage(error, isArabic);

                        // Update status to error
                        setUploadingImages((prev) =>
                            updateUploadStatus(prev, tempId, 'error', 0)
                        );

                        setErrors((prev) =>
                            updateErrorState(prev, tempId, errorMsg)
                        );

                        // Update queue item status
                        setUploadQueue((prev) =>
                            updateQueueItemStatus(prev, tempId, 'error')
                        );

                        // Show error message
                        showUploadError(errorMsg, isArabic);

                        reject(error);
                    },
                });
            });

        } catch (error) {
            console.error("Upload error:", error);

            const errorMsg = extractErrorMessage(error, isArabic);

            setUploadingImages((prev) =>
                updateUploadStatus(prev, tempId, 'error', 0)
            );

            setErrors((prev) =>
                updateErrorState(prev, tempId, errorMsg)
            );

            setUploadQueue((prev) =>
                updateQueueItemStatus(prev, tempId, 'error')
            );

            showUploadError(errorMsg, isArabic);
        }
    }, [uploadMedia, onChange, value, maxSizeMB, isArabic]);

    // ✅ Process upload queue sequentially
    useEffect(() => {
        const processNext = async () => {
            if (isProcessing) return;

            const nextItem = getNextPendingItem(uploadQueue, uploadingImages);

            if (!nextItem) {
                setIsProcessing(false);
                return;
            }

            setIsProcessing(true);
            await uploadSingleImage(nextItem.id, nextItem.file);
            setIsProcessing(false);
        };

        processNext();
    }, [uploadQueue, isProcessing, uploadingImages, uploadSingleImage]);

    // ✅ Retry failed upload
    const retryUpload = useCallback((tempId) => {
        setErrors((prev) => removeErrorState(prev, tempId));
        setUploadingImages((prev) => removeUploadStatus(prev, tempId));
        setUploadQueue((prev) => updateQueueItemStatus(prev, tempId, 'pending'));
    }, []);

    // ✅ Remove from queue
    const removeFromUploadQueue = useCallback((tempId) => {
        setUploadQueue((prev) => removeFromQueue(prev, tempId));
        setUploadingImages((prev) => removeUploadStatus(prev, tempId));
        setErrors((prev) => removeErrorState(prev, tempId));
    }, []);

    return {
        uploadQueue,
        setUploadQueue,
        uploadingImages,
        errors,
        retryUpload,
        removeFromUploadQueue,
    };
};

export default useImageUploadQueue;
